// Passing a call to a colleague. The agent picks someone who is free right
// now - or "whoever is free next" - and may add a line for them. A blind
// transfer: once it goes through, this agent is off the call and the caller
// holds while the colleague's phone rings.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowRightLeft, Loader2, UserPlus } from 'lucide-react';
import { voiceService, type RosterAgent } from '@/services/voice.service';
import { useAuthStore } from '@stores/authStore';
import { useVoiceAgentStore } from '@stores/voiceAgentStore';

const NEXT_AVAILABLE = '';
const MAX_NOTE = 300;
const LABEL = 'co-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]';

/** Colleagues who can take a call now: checked in, free, and not the agent themselves. */
export function transferTargets(roster: RosterAgent[], currentUserId: string | undefined): RosterAgent[] {
  return roster
    .filter((agent) => agent.status === 'available' && !agent.stale && agent.agentId !== currentUserId)
    .sort((a, b) => (a.agentName || '').localeCompare(b.agentName || ''));
}

/** Words that differ between passing the call on and adding someone to it. */
const COPY = {
  transfer: {
    title: 'Transfer call',
    explain: "You leave the call as soon as it is transferred. The caller holds while your colleague's phone rings.",
    legend: 'Transfer to',
    nobody: 'Nobody else is available to take a call right now.',
    action: 'Transfer',
    busy: 'Transferring…',
    failed: 'The call could not be transferred. It is still with you.',
  },
  conference: {
    title: 'Add to call',
    explain: 'Your colleague joins you and the caller once they answer; you all hear each other. If you then leave, they keep the caller.',
    legend: 'Add',
    nobody: 'Nobody else is available to join the call right now.',
    action: 'Add to call',
    busy: 'Calling them…',
    failed: 'Your colleague could not be added. The call is unchanged.',
  },
} as const;

export function TransferDialog({ onClose, mode = 'transfer' }: { onClose: () => void; mode?: 'transfer' | 'conference' }) {
  const copy = COPY[mode];
  const currentUserId = useAuthStore((s) => s.user?.id);
  const transferCall = useVoiceAgentStore((s) => s.transferCall);
  const addToCall = useVoiceAgentStore((s) => s.addToCall);
  // A colleague can be added once the caller is connected to this agent.
  const waitingToConnect = useVoiceAgentStore((s) => mode === 'conference' && !s.callLive);
  const [targets, setTargets] = useState<RosterAgent[] | null>(null);
  const [choice, setChoice] = useState<string>(NEXT_AVAILABLE);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A ref as well as state: two quick clicks land before a re-render.
  const sendingRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const available = transferTargets(await voiceService.getAgentRoster(), currentUserId);
      setTargets(available);
      // Whoever was picked may have taken another call since.
      setChoice((current) => (available.some((a) => a.agentId === current) ? current : NEXT_AVAILABLE));
    } catch {
      setTargets([]);
      setError('The team list could not be loaded. Try again in a moment.');
    }
  }, [currentUserId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !sendingRef.current) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async () => {
    if (sendingRef.current || !targets?.length) return;
    sendingRef.current = true;
    setSending(true);
    setError(null);
    const colleague = targets.find((a) => a.agentId === choice);
    try {
      const to = colleague ? { agentId: colleague.agentId, name: colleague.agentName || 'your colleague' } : {};
      if (mode === 'conference') await addToCall(to);
      else await transferCall(to, note.trim() || undefined);
      onClose();
    } catch (err: any) {
      // Refused: the call is still this agent's. Say why, and show who is free now.
      setError(String(err?.response?.data?.message || copy.failed));
      await load();
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const nobody = targets !== null && targets.length === 0;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="transfer-call-title"
    >
      <div
        className="w-full max-w-md overflow-hidden border border-[var(--co-line)] bg-[var(--co-raised)]"
        style={{ borderRadius: 14, boxShadow: 'var(--co-shadow-card)' }}
      >
        <div className="border-b border-[var(--co-line)] px-6 py-4">
          <h2 id="transfer-call-title" className="text-[17px] font-bold text-[var(--co-ink)]">
            {copy.title}
          </h2>
          <p className="mt-1 text-[12.5px] leading-[1.5] text-[var(--co-ink-3)]">{copy.explain}</p>
        </div>

        <div className="max-h-[55vh] space-y-4 overflow-y-auto px-6 py-4">
          {targets === null ? (
            <p className="flex items-center gap-2 py-4 text-[12.5px] text-[var(--co-ink-3)]">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Checking who is free…
            </p>
          ) : nobody ? (
            <p className="py-2 text-[13px] leading-[1.5] text-[var(--co-ink-2)]">
              {copy.nobody}
            </p>
          ) : (
            <fieldset>
              <legend className={LABEL}>{copy.legend}</legend>
              <div className="mt-2 space-y-[6px]">
                <TargetOption
                  value={NEXT_AVAILABLE}
                  checked={choice === NEXT_AVAILABLE}
                  onChange={setChoice}
                  title="Next available agent"
                  hint="Whoever has been free the longest"
                />
                {targets.map((agent) => (
                  <TargetOption
                    key={agent.agentId}
                    value={agent.agentId}
                    checked={choice === agent.agentId}
                    onChange={setChoice}
                    title={agent.agentName || 'Agent'}
                    hint="Available"
                  />
                ))}
              </div>
            </fieldset>
          )}

          {mode === 'transfer' && !nobody && targets !== null && (
            <label className="block">
              <span className={LABEL}>Note for them (optional)</span>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, MAX_NOTE))}
                rows={2}
                maxLength={MAX_NOTE}
                placeholder="What they should know before they pick up"
                className="mt-2 w-full resize-none rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 py-2 text-[13px] text-[var(--co-ink)] focus:border-[var(--co-brass)] focus:outline-none"
              />
            </label>
          )}

          {error && (
            <p role="alert" className="text-[12.5px] leading-[1.5] text-[var(--co-burgundy)]">
              {error}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-[var(--co-line)] px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="h-10 rounded-[10px] border border-[var(--co-line-strong)] px-4 text-[13px] font-semibold text-[var(--co-ink-2)] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={sending || targets === null || nobody || waitingToConnect}
            aria-busy={sending}
            className="flex h-10 items-center gap-2 rounded-[10px] bg-[var(--co-brass-ink)] px-4 text-[13px] font-bold text-[var(--co-brass-on)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : mode === 'conference' ? (
              <UserPlus className="h-4 w-4" aria-hidden />
            ) : (
              <ArrowRightLeft className="h-4 w-4" aria-hidden />
            )}
            {sending ? copy.busy : waitingToConnect ? 'Connecting…' : copy.action}
          </button>
        </div>
      </div>
    </div>
  );
}

function TargetOption({
  value,
  checked,
  onChange,
  title,
  hint,
}: {
  value: string;
  checked: boolean;
  onChange: (value: string) => void;
  title: string;
  hint: string;
}) {
  return (
    <label
      className="flex cursor-pointer items-center gap-3 rounded-[10px] border px-3 py-[9px]"
      style={
        checked
          ? { borderColor: 'var(--co-brass)', background: 'var(--co-brass-wash)' }
          : { borderColor: 'var(--co-line)', background: 'var(--co-raised)' }
      }
    >
      <input
        type="radio"
        name="transfer-target"
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
        className="h-[14px] w-[14px] accent-[var(--co-brass-ink)]"
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-semibold text-[var(--co-ink)]">{title}</span>
        <span className="block text-[11.5px] text-[var(--co-ink-3)]">{hint}</span>
      </span>
    </label>
  );
}
